import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Mic,
  MicOff,
  Play,
  Square,
  Upload,
  Users,
  Clock,
  FileText,
  CheckCircle,
  AlertCircle,
  Plus,
  Calendar,
  MapPin,
  Loader2,
  Phone,
  PhoneOff,
  Volume2,
  RefreshCw,
  Download,
  ChevronRight,
  Save,
  Trash2,
  ExternalLink,
  CircleDot,
  History,
  ListTodo,
  Mail,
  Send,
  Brain
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// IndexedDB helper for storing audio chunks locally
const DB_NAME = 'MeetingRecordingsDB';
const STORE_NAME = 'audioChunks';

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
};

const saveChunkToDB = async (meetingId, chunk, timestamp) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.add({ meetingId, chunk, timestamp, uploaded: false });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const getChunksFromDB = async (meetingId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const chunks = request.result.filter(c => c.meetingId === meetingId && !c.uploaded);
      resolve(chunks);
    };
    request.onerror = () => reject(request.error);
  });
};

const clearChunksFromDB = async (meetingId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const chunks = request.result.filter(c => c.meetingId === meetingId);
      chunks.forEach(chunk => store.delete(chunk.id));
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
};

export default function MeetingMinutes({ user }) {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  
  // State
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('meetings'); // 'meetings' | 'actions'
  const [allActionItems, setAllActionItems] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  
  // Create meeting form
  const [newMeeting, setNewMeeting] = useState({
    title: "",
    scheduled_date: "",
    scheduled_time: "",
    location: "",
    agenda: ""
  });
  
  // Recording state - Record & Forget
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [savedChunksCount, setSavedChunksCount] = useState(0);
  const [hasPendingRecording, setHasPendingRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunkIntervalRef = useRef(null);
  
  // Historical minutes upload
  const [uploadingMinutes, setUploadingMinutes] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  
  // Dial-in state
  const [isDialedIn, setIsDialedIn] = useState(false);
  const [wsConnection, setWsConnection] = useState(null);
  
  // Pre-meeting brief
  const [preMeetingBrief, setPreMeetingBrief] = useState(null);
  const [showBriefDialog, setShowBriefDialog] = useState(false);
  
  // Email minutes
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // AI Action Analysis
  const [showActionReviewDialog, setShowActionReviewDialog] = useState(false);
  const [actionAnalysis, setActionAnalysis] = useState(null);
  const [analyzingActions, setAnalyzingActions] = useState(false);
  const [applyingActions, setApplyingActions] = useState(false);
  const [selectedUpdates, setSelectedUpdates] = useState([]);
  const [selectedNewItems, setSelectedNewItems] = useState([]);
  const [selectedRollovers, setSelectedRollovers] = useState([]);
  
  // Demo mode
  const [creatingDemo, setCreatingDemo] = useState(false);

  useEffect(() => {
    fetchMeetings();
    fetchAllActionItems();
    if (meetingId) {
      fetchMeeting(meetingId);
      checkPendingRecording(meetingId);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (chunkIntervalRef.current) clearInterval(chunkIntervalRef.current);
      if (wsConnection) wsConnection.close();
    };
  }, [meetingId]);

  // Check for pending recordings when meeting loads
  const checkPendingRecording = async (mId) => {
    try {
      const chunks = await getChunksFromDB(mId);
      if (chunks.length > 0) {
        setHasPendingRecording(true);
        setSavedChunksCount(chunks.length);
        const totalSeconds = chunks.length * 10; // Each chunk is ~10 seconds
        setRecordingTime(totalSeconds);
        toast.info(`Found ${Math.floor(totalSeconds / 60)} mins of saved recording ready to upload`);
      }
    } catch (error) {
      console.error("Error checking pending recordings:", error);
    }
  };

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/meetings/list`, {
        withCredentials: true
      });
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error("Error fetching meetings:", error);
      toast.error("Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllActionItems = async () => {
    try {
      setLoadingActions(true);
      const response = await axios.get(`${API}/api/meetings/action-items/outstanding`, {
        withCredentials: true
      });
      setAllActionItems(response.data.action_items || []);
    } catch (error) {
      console.error("Error fetching action items:", error);
    } finally {
      setLoadingActions(false);
    }
  };

  const fetchMeeting = async (id) => {
    try {
      const response = await axios.get(`${API}/api/meetings/${id}`, {
        withCredentials: true
      });
      setSelectedMeeting(response.data);
    } catch (error) {
      console.error("Error fetching meeting:", error);
      toast.error("Failed to load meeting");
    }
  };

  const createMeeting = async () => {
    if (!newMeeting.title || !newMeeting.scheduled_date) {
      toast.error("Please fill in title and date");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/meetings/create`, newMeeting, {
        withCredentials: true
      });
      toast.success("Meeting created!");
      setShowCreateDialog(false);
      setNewMeeting({ title: "", scheduled_date: "", scheduled_time: "", location: "", agenda: "" });
      fetchMeetings();
      navigate(`/meeting-minutes/${response.data.meeting_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create meeting");
    }
  };

  const startMeeting = async () => {
    try {
      await axios.post(`${API}/api/meetings/${selectedMeeting.id}/start`, {}, {
        withCredentials: true
      });
      toast.success("Meeting started!");
      fetchMeeting(selectedMeeting.id);
    } catch (error) {
      toast.error("Failed to start meeting");
    }
  };

  const endMeeting = async () => {
    try {
      await axios.post(`${API}/api/meetings/${selectedMeeting.id}/end`, {}, {
        withCredentials: true
      });
      toast.success("Meeting ended, generating minutes...");
      fetchMeeting(selectedMeeting.id);
    } catch (error) {
      toast.error("Failed to end meeting");
    }
  };

  const joinMeeting = async () => {
    try {
      await axios.post(`${API}/api/meetings/${selectedMeeting.id}/join`, {}, {
        withCredentials: true
      });
      toast.success("Joined meeting!");
      fetchMeeting(selectedMeeting.id);
    } catch (error) {
      toast.error("Failed to join meeting");
    }
  };

  // ============================================
  // RECORD & FORGET - Background Recording
  // ============================================
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      // Save chunk every time data is available
      mediaRecorderRef.current.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          try {
            // Convert blob to array buffer for storage
            const arrayBuffer = await event.data.arrayBuffer();
            await saveChunkToDB(selectedMeeting.id, arrayBuffer, Date.now());
            setSavedChunksCount(prev => prev + 1);
          } catch (err) {
            console.error("Error saving chunk:", err);
          }
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };
      
      // Request data every 10 seconds for chunked saving
      mediaRecorderRef.current.start(10000);
      setIsRecording(true);
      setRecordingTime(0);
      setSavedChunksCount(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success("Recording started - you can switch apps, recording saves automatically");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Failed to access microphone. Please allow microphone access.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setHasPendingRecording(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      toast.success(`Recording saved locally (${savedChunksCount} chunks). Ready to upload.`);
    }
  };

  const uploadSavedRecording = async () => {
    try {
      setUploading(true);
      
      // Get all saved chunks
      const chunks = await getChunksFromDB(selectedMeeting.id);
      if (chunks.length === 0) {
        toast.error("No recording found");
        return;
      }
      
      // Combine chunks into single blob
      const arrayBuffers = chunks.map(c => c.chunk);
      const combinedBlob = new Blob(arrayBuffers, { type: 'audio/webm' });
      
      // Upload
      const formData = new FormData();
      formData.append('audio', combinedBlob, 'recording.webm');
      formData.append('speaker_name', user?.name || 'Unknown');
      formData.append('speaker_id', user?.id || '');
      
      await axios.post(
        `${API}/api/meetings/${selectedMeeting.id}/upload-audio`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      // Clear local storage
      await clearChunksFromDB(selectedMeeting.id);
      
      toast.success("Recording uploaded! Transcription in progress...");
      setHasPendingRecording(false);
      setSavedChunksCount(0);
      setRecordingTime(0);
      fetchMeeting(selectedMeeting.id);
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(error.response?.data?.detail || "Failed to upload recording");
    } finally {
      setUploading(false);
    }
  };

  const discardRecording = async () => {
    if (window.confirm("Are you sure you want to discard this recording?")) {
      await clearChunksFromDB(selectedMeeting.id);
      setHasPendingRecording(false);
      setSavedChunksCount(0);
      setRecordingTime(0);
      toast.success("Recording discarded");
    }
  };

  // ============================================
  // ACTION ITEM STATUS UPDATE
  // ============================================
  
  const updateActionStatus = async (actionId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/meetings/action-items/${actionId}/status`,
        null,
        {
          params: { status: newStatus },
          withCredentials: true
        }
      );
      toast.success(`Action marked as ${newStatus}`);
      fetchAllActionItems();
      if (selectedMeeting) {
        fetchMeeting(selectedMeeting.id);
      }
    } catch (error) {
      console.error("Error updating action status:", error);
      toast.error("Failed to update action status");
    }
  };

  // ============================================
  // HISTORICAL MINUTES UPLOAD
  // ============================================
  
  const uploadHistoricalMinutes = async () => {
    if (!uploadFile) {
      toast.error("Please select a file");
      return;
    }
    
    try {
      setUploadingMinutes(true);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(
        `${API}/api/meetings/upload-historical`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      toast.success("Minutes uploaded! AI is extracting action items...");
      setShowUploadDialog(false);
      setUploadFile(null);
      
      // Refresh meetings and action items
      fetchMeetings();
      fetchAllActionItems();
      
      // Navigate to the new meeting
      if (response.data.meeting_id) {
        navigate(`/meeting-minutes/${response.data.meeting_id}`);
      }
    } catch (error) {
      console.error("Error uploading minutes:", error);
      toast.error(error.response?.data?.detail || "Failed to upload minutes");
    } finally {
      setUploadingMinutes(false);
    }
  };

  const regenerateMinutes = async () => {
    try {
      await axios.post(`${API}/api/meetings/${selectedMeeting.id}/regenerate-minutes`, {}, {
        withCredentials: true
      });
      toast.success("Regenerating minutes...");
      setTimeout(() => fetchMeeting(selectedMeeting.id), 3000);
    } catch (error) {
      toast.error("Failed to regenerate minutes");
    }
  };

  // Pre-meeting Brief
  const fetchPreMeetingBrief = async () => {
    try {
      const response = await axios.get(`${API}/api/meetings/pre-meeting-brief`, {
        withCredentials: true
      });
      setPreMeetingBrief(response.data);
      setShowBriefDialog(true);
    } catch (error) {
      console.error("Error fetching brief:", error);
      toast.error("Failed to load pre-meeting brief");
    }
  };

  // Email Minutes
  const emailMinutes = async () => {
    try {
      setSendingEmail(true);
      const emails = emailRecipients.split(',').map(e => e.trim()).filter(e => e);
      
      await axios.post(
        `${API}/api/meetings/${selectedMeeting.id}/email-minutes`,
        { recipient_emails: emails },
        { withCredentials: true }
      );
      
      toast.success("Minutes emailed successfully!");
      setShowEmailDialog(false);
      setEmailRecipients("");
    } catch (error) {
      console.error("Error emailing minutes:", error);
      toast.error(error.response?.data?.detail || "Failed to email minutes");
    } finally {
      setSendingEmail(false);
    }
  };

  // AI Action Analysis Functions
  const analyzeActionsWithAI = async () => {
    if (!selectedMeeting?.id) return;
    
    try {
      setAnalyzingActions(true);
      const response = await axios.post(
        `${API}/api/meetings/${selectedMeeting.id}/analyze-actions`,
        {},
        { withCredentials: true }
      );
      
      setActionAnalysis(response.data.analysis);
      
      // Pre-select all suggestions by default
      if (response.data.analysis.action_updates) {
        setSelectedUpdates(response.data.analysis.action_updates.map((_, i) => i));
      }
      if (response.data.analysis.new_action_items) {
        setSelectedNewItems(response.data.analysis.new_action_items.map((_, i) => i));
      }
      if (response.data.analysis.rollover_items) {
        setSelectedRollovers(response.data.analysis.rollover_items.map(item => item.action_id));
      }
      
      setShowActionReviewDialog(true);
      toast.success("AI analysis complete - please review suggestions");
    } catch (error) {
      console.error("Error analyzing actions:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze actions");
    } finally {
      setAnalyzingActions(false);
    }
  };

  const applyActionUpdates = async () => {
    if (!selectedMeeting?.id || !actionAnalysis) return;
    
    try {
      setApplyingActions(true);
      
      // Build request with selected items
      const approvedUpdates = selectedUpdates.map(idx => {
        const update = actionAnalysis.action_updates[idx];
        return {
          action_id: update.action_id,
          new_status: update.suggested_status,
          update_notes: update.suggested_notes,
          approved: true
        };
      });
      
      const approvedNewItems = selectedNewItems.map(idx => actionAnalysis.new_action_items[idx]);
      
      const response = await axios.post(
        `${API}/api/meetings/${selectedMeeting.id}/apply-action-updates`,
        {
          meeting_id: selectedMeeting.id,
          approved_updates: approvedUpdates,
          approved_new_items: approvedNewItems,
          rollover_items: selectedRollovers
        },
        { withCredentials: true }
      );
      
      const results = response.data.results;
      toast.success(`Applied: ${results.updated.length} updates, ${results.created.length} new items, ${results.rolled_over.length} rollovers`);
      
      setShowActionReviewDialog(false);
      setActionAnalysis(null);
      
      // Refresh action items
      fetchAllActionItems();
      if (selectedMeeting) {
        fetchMeeting(selectedMeeting.id);
      }
    } catch (error) {
      console.error("Error applying actions:", error);
      toast.error(error.response?.data?.detail || "Failed to apply updates");
    } finally {
      setApplyingActions(false);
    }
  };

  const toggleUpdateSelection = (idx) => {
    setSelectedUpdates(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const toggleNewItemSelection = (idx) => {
    setSelectedNewItems(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const toggleRolloverSelection = (actionId) => {
    setSelectedRollovers(prev => 
      prev.includes(actionId) ? prev.filter(id => id !== actionId) : [...prev, actionId]
    );
  };

  // Create Demo Meeting
  const createDemoMeeting = async () => {
    try {
      setCreatingDemo(true);
      const response = await axios.post(
        `${API}/api/meetings/create-demo`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      // Refresh meetings list
      await fetchMeetings();
      
      // Navigate to the new demo meeting
      navigate(`/meeting-minutes/${response.data.meeting_id}`);
      
    } catch (error) {
      console.error("Error creating demo:", error);
      toast.error(error.response?.data?.detail || "Failed to create demo meeting");
    } finally {
      setCreatingDemo(false);
    }
  };

  // Dial-in Functions (WebSocket)
  const dialIn = () => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/api/meetings/${selectedMeeting.id}/dial-in`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'register', name: user?.name || 'Remote User' }));
      setIsDialedIn(true);
      setWsConnection(ws);
      toast.success("Dialed in to meeting");
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'audio_chunk') {
        console.log("Received audio from:", data.from);
      }
    };
    
    ws.onclose = () => {
      setIsDialedIn(false);
      setWsConnection(null);
    };
    
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      toast.error("Failed to connect");
    };
  };

  const hangUp = () => {
    if (wsConnection) {
      wsConnection.send(JSON.stringify({ type: 'leave' }));
      wsConnection.close();
    }
    setIsDialedIn(false);
    setWsConnection(null);
    toast.success("Left the call");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-SG', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-green-500/20 text-green-400';
      case 'processing': return 'bg-yellow-500/20 text-yellow-400';
      case 'completed': return 'bg-purple-500/20 text-purple-400';
      case 'historical': return 'bg-slate-500/20 text-slate-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getActionStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('closed') || s.includes('completed') || s.includes('done')) {
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
    if (s.includes('ongoing') || s.includes('progress')) {
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  // Render meeting list view
  if (!selectedMeeting) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center">
                <FileText className="w-8 h-8 mr-3 text-yellow-500" />
                Meeting Minutes
              </h1>
              <p className="text-slate-400 mt-2">
                AI-powered transcription, minutes generation & action tracking
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={fetchPreMeetingBrief}
                variant="outline"
                className="border-purple-500 text-purple-400"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Pre-Meeting Brief
              </Button>
              <Button
                onClick={() => setShowUploadDialog(true)}
                variant="outline"
                className="border-blue-500 text-blue-400"
              >
                <History className="w-4 h-4 mr-2" />
                Upload Past Minutes
              </Button>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Meeting
              </Button>
              <Button
                onClick={createDemoMeeting}
                disabled={creatingDemo}
                variant="outline"
                className="border-green-500 text-green-400"
                title="Create a demo meeting with mock transcript to test AI action workflow"
              >
                {creatingDemo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Quick Demo
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
            <Button
              variant={activeTab === 'meetings' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('meetings')}
              className={activeTab === 'meetings' ? 'bg-yellow-500 text-black' : 'text-slate-400'}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Meetings
            </Button>
            <Button
              variant={activeTab === 'actions' ? 'default' : 'ghost'}
              onClick={() => setActiveTab('actions')}
              className={activeTab === 'actions' ? 'bg-yellow-500 text-black' : 'text-slate-400'}
            >
              <ListTodo className="w-4 h-4 mr-2" />
              Outstanding Actions
              {allActionItems.filter(a => !a.status?.toLowerCase().includes('closed')).length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {allActionItems.filter(a => !a.status?.toLowerCase().includes('closed')).length}
                </span>
              )}
            </Button>
          </div>

          {/* Meetings Tab */}
          {activeTab === 'meetings' && (
            <>
              {loading ? (
                <Card className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-yellow-500" />
                  <p className="text-slate-400 mt-4">Loading meetings...</p>
                </Card>
              ) : meetings.length === 0 ? (
                <Card className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-slate-500 mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">No Meetings Yet</h3>
                  <p className="text-slate-400 mb-4">
                    Create a new meeting or upload past minutes to get started
                  </p>
                  <div className="flex justify-center gap-2">
                    <Button
                      onClick={() => setShowUploadDialog(true)}
                      variant="outline"
                      className="border-blue-500 text-blue-400"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Upload Past Minutes
                    </Button>
                    <Button
                      onClick={() => setShowCreateDialog(true)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Meeting
                    </Button>
                  </div>
                </Card>
              ) : (
                <div className="space-y-4">
                  {meetings.map((meeting) => (
                    <Card
                      key={meeting.id}
                      className="p-4 hover:border-yellow-500/30 cursor-pointer transition-all"
                      onClick={() => navigate(`/meeting-minutes/${meeting.id}`)}
                      data-testid={`meeting-card-${meeting.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-bold text-white text-lg">{meeting.title}</h3>
                            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(meeting.status)}`}>
                              {meeting.status === 'historical' ? 'Imported' : meeting.status?.replace('_', ' ')}
                            </span>
                            {meeting.has_action_items && (
                              <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400">
                                Has Actions
                              </span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(meeting.scheduled_date)}
                            </span>
                            {meeting.scheduled_time && (
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {meeting.scheduled_time}
                              </span>
                            )}
                            {meeting.location && (
                              <span className="flex items-center">
                                <MapPin className="w-4 h-4 mr-1" />
                                {meeting.location}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Users className="w-4 h-4 mr-1" />
                              {meeting.actual_attendees?.length || meeting.attendees?.length || 0} attendees
                            </span>
                          </div>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Outstanding Actions Tab */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              {loadingActions ? (
                <Card className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-yellow-500" />
                </Card>
              ) : allActionItems.length === 0 ? (
                <Card className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">No Outstanding Actions</h3>
                  <p className="text-slate-400">All action items have been completed!</p>
                </Card>
              ) : (
                <>
                  {/* Filter summary */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-sm text-slate-400">
                      {allActionItems.filter(a => !['closed', 'done', 'completed'].includes((a.status || '').toLowerCase())).length} open items
                    </span>
                  </div>
                  
                  <div className="grid gap-3">
                    {allActionItems.map((item, i) => {
                      const isClosed = ['closed', 'done', 'completed'].includes((item.status || '').toLowerCase());
                      return (
                        <Card 
                          key={item.id || i} 
                          className={`p-4 border ${getActionStatusColor(item.status)} ${isClosed ? 'opacity-60' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className={`font-medium ${isClosed ? 'text-slate-400 line-through' : 'text-white'}`}>
                                {item.task}
                              </p>
                              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                                <span className="text-yellow-400">
                                  Owner: {item.owner}
                                </span>
                                {item.due_date && (
                                  <span className="text-slate-400">
                                    Due: {item.due_date}
                                  </span>
                                )}
                                <span className="text-slate-500">
                                  From: {item.meeting_title || 'Unknown Meeting'}
                                </span>
                              </div>
                              {item.update_notes && (
                                <div className="text-sm text-green-300 bg-green-500/10 p-2 rounded mt-2">
                                  <strong>Update:</strong> {item.update_notes}
                                </div>
                              )}
                              {item.notes && !item.update_notes && (
                                <p className="text-sm text-slate-400 mt-2">{item.notes}</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${getActionStatusColor(item.status)}`}>
                                {item.status || 'Pending'}
                              </span>
                              {!isClosed && item.id && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => updateActionStatus(item.id, 'Done')}
                                    className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-7 px-2 text-xs"
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Done
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Create Meeting Dialog */}
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Meeting</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Meeting Title *</label>
                  <Input
                    placeholder="e.g., Football Committee Meeting"
                    value={newMeeting.title}
                    onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-1 block">Date *</label>
                    <Input
                      type="date"
                      value={newMeeting.scheduled_date}
                      onChange={(e) => setNewMeeting({...newMeeting, scheduled_date: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-1 block">Time</label>
                    <Input
                      type="time"
                      value={newMeeting.scheduled_time}
                      onChange={(e) => setNewMeeting({...newMeeting, scheduled_time: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Location</label>
                  <Input
                    placeholder="e.g., SCC Committee Room"
                    value={newMeeting.location}
                    onChange={(e) => setNewMeeting({...newMeeting, location: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Agenda</label>
                  <Textarea
                    placeholder="Meeting agenda items..."
                    value={newMeeting.agenda}
                    onChange={(e) => setNewMeeting({...newMeeting, agenda: e.target.value})}
                    className="bg-slate-800 border-slate-700 min-h-[100px]"
                  />
                </div>
                
                <Button
                  onClick={createMeeting}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Create Meeting
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Upload Historical Minutes Dialog */}
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <History className="w-5 h-5 mr-2 text-blue-400" />
                  Upload Past Meeting Minutes
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <p className="text-slate-400 text-sm">
                  Upload your existing meeting minutes (PDF or Word). Our AI will extract:
                </p>
                <ul className="text-sm text-slate-300 space-y-1 ml-4">
                  <li>• Attendees list</li>
                  <li>• Discussion points</li>
                  <li>• Action items with owners and due dates</li>
                  <li>• Outstanding matters for follow-up</li>
                </ul>
                
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="hidden"
                    id="minutes-upload"
                  />
                  <label htmlFor="minutes-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                    {uploadFile ? (
                      <p className="text-white font-medium">{uploadFile.name}</p>
                    ) : (
                      <>
                        <p className="text-white font-medium">Click to select file</p>
                        <p className="text-slate-400 text-sm mt-1">PDF or Word document</p>
                      </>
                    )}
                  </label>
                </div>
                
                <Button
                  onClick={uploadHistoricalMinutes}
                  disabled={!uploadFile || uploadingMinutes}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                >
                  {uploadingMinutes ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing with AI...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Extract Action Items
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Pre-Meeting Brief Dialog - List View */}
          <Dialog open={showBriefDialog} onOpenChange={setShowBriefDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-purple-400" />
                  Pre-Meeting Brief
                </DialogTitle>
              </DialogHeader>
              
              {preMeetingBrief && (
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {preMeetingBrief.total_outstanding} Outstanding Actions
                    </h3>
                    <p className="text-slate-400 text-sm">
                      Items requiring follow-up from previous meetings
                    </p>
                  </div>
                  
                  {/* By Owner */}
                  {preMeetingBrief.by_owner && Object.keys(preMeetingBrief.by_owner).length > 0 && (
                    <div>
                      <h4 className="font-semibold text-yellow-400 mb-3">By Owner</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(preMeetingBrief.by_owner).map(([owner, count]) => (
                          <span key={owner} className="px-3 py-1 bg-slate-800 rounded-full text-sm">
                            <span className="text-white">{owner}</span>
                            <span className="text-yellow-400 ml-2">{count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Items */}
                  <div>
                    <h4 className="font-semibold text-red-400 mb-3">Outstanding Items</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {preMeetingBrief.items?.map((item, i) => (
                        <div key={i} className="p-3 bg-slate-800/50 rounded border border-slate-700">
                          <p className="text-white text-sm">{item.task}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span>Owner: <span className="text-yellow-400">{item.owner}</span></span>
                            <span>From: {item.meeting_title?.slice(0, 30)}...</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => setShowBriefDialog(false)}
                    className="w-full bg-purple-500 hover:bg-purple-600"
                  >
                    Close Brief
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  // Render single meeting view
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedMeeting(null);
              navigate("/meeting-minutes");
            }}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Meetings
          </Button>
        </div>

        {/* Meeting Header */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-white">{selectedMeeting.title}</h1>
                <span className={`text-xs px-2 py-1 rounded ${getStatusColor(selectedMeeting.status)}`}>
                  {selectedMeeting.status === 'historical' ? 'Imported' : selectedMeeting.status?.replace('_', ' ')}
                </span>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(selectedMeeting.scheduled_date)}
                </span>
                {selectedMeeting.scheduled_time && (
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {selectedMeeting.scheduled_time}
                  </span>
                )}
                {selectedMeeting.location && (
                  <span className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {selectedMeeting.location}
                  </span>
                )}
              </div>
            </div>
            
            {/* Meeting Controls */}
            <div className="flex items-center gap-2">
              {selectedMeeting.status === 'scheduled' && (
                <>
                  <Button onClick={joinMeeting} variant="outline" className="border-green-500 text-green-400">
                    <Users className="w-4 h-4 mr-2" />
                    Join
                  </Button>
                  <Button onClick={startMeeting} className="bg-green-500 hover:bg-green-600 text-white">
                    <Play className="w-4 h-4 mr-2" />
                    Start Meeting
                  </Button>
                </>
              )}
              
              {selectedMeeting.status === 'in_progress' && (
                <Button onClick={endMeeting} className="bg-red-500 hover:bg-red-600 text-white">
                  <Square className="w-4 h-4 mr-2" />
                  End Meeting
                </Button>
              )}
            </div>
          </div>
          
          {selectedMeeting.agenda && (
            <div className="mt-4 p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-medium text-white mb-2">Agenda</h3>
              <p className="text-slate-400 text-sm whitespace-pre-wrap">{selectedMeeting.agenda}</p>
            </div>
          )}
        </Card>

        {/* Recording Section - Only show when meeting is in progress */}
        {selectedMeeting.status === 'in_progress' && (
          <Card className="p-6 mb-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Mic className="w-5 h-5 mr-2 text-green-400" />
              Voice Recording
              {isRecording && (
                <span className="ml-3 flex items-center text-red-400 text-sm">
                  <CircleDot className="w-4 h-4 mr-1 animate-pulse" />
                  Recording...
                </span>
              )}
            </h2>
            
            <p className="text-slate-400 text-sm mb-4">
              <strong>Record & Forget:</strong> Start recording, then switch apps freely. 
              Audio saves automatically every 10 seconds. Come back anytime to upload.
            </p>
            
            <div className="flex flex-wrap items-center gap-4">
              {!isRecording && !hasPendingRecording ? (
                <Button
                  onClick={startRecording}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  disabled={uploading}
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Start Recording
                </Button>
              ) : isRecording ? (
                <>
                  <Button
                    onClick={stopRecording}
                    variant="outline"
                    className="border-red-500 text-red-400"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Stop ({formatTime(recordingTime)})
                  </Button>
                  <span className="text-sm text-green-400 flex items-center">
                    <Save className="w-4 h-4 mr-1" />
                    {savedChunksCount} chunks saved
                  </span>
                </>
              ) : hasPendingRecording && (
                <>
                  <div className="flex items-center gap-2 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
                    <Save className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-300">
                      {formatTime(recordingTime)} recording ready to upload
                    </span>
                  </div>
                  <Button
                    onClick={uploadSavedRecording}
                    disabled={uploading}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload & Transcribe
                  </Button>
                  <Button
                    onClick={discardRecording}
                    variant="ghost"
                    className="text-red-400"
                    disabled={uploading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Discard
                  </Button>
                </>
              )}
            </div>
            
            {/* Dial-in Option */}
            <div className="mt-4 pt-4 border-t border-slate-700">
              <h3 className="font-medium text-white mb-2 flex items-center">
                <Phone className="w-4 h-4 mr-2 text-blue-400" />
                Remote Dial-In
              </h3>
              <p className="text-slate-400 text-sm mb-3">
                Can't attend physically? Dial in to listen and speak remotely.
              </p>
              
              {!isDialedIn ? (
                <Button onClick={dialIn} variant="outline" className="border-blue-500 text-blue-400">
                  <Phone className="w-4 h-4 mr-2" />
                  Dial In
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-green-400 flex items-center">
                    <Volume2 className="w-4 h-4 mr-2 animate-pulse" />
                    Connected
                  </span>
                  <Button onClick={hangUp} variant="outline" className="border-red-500 text-red-400">
                    <PhoneOff className="w-4 h-4 mr-2" />
                    Hang Up
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Attendees */}
        {(selectedMeeting.actual_attendees?.length > 0 || selectedMeeting.attendees?.length > 0) && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-400" />
              Attendees
            </h2>
            
            {selectedMeeting.attendees?.present && (
              <div className="mb-3">
                <p className="text-sm text-slate-400 mb-2">Present:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedMeeting.attendees.present.map((name, i) => (
                    <span key={i} className="px-3 py-1 rounded-full text-sm bg-green-500/20 text-green-300">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {selectedMeeting.attendees?.apologies && selectedMeeting.attendees.apologies.length > 0 && (
              <div>
                <p className="text-sm text-slate-400 mb-2">Apologies:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedMeeting.attendees.apologies.map((name, i) => (
                    <span key={i} className="px-3 py-1 rounded-full text-sm bg-slate-500/20 text-slate-300">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {selectedMeeting.actual_attendees?.length > 0 && !selectedMeeting.attendees?.present && (
              <div className="flex flex-wrap gap-2">
                {selectedMeeting.actual_attendees.map((attendee, i) => (
                  <span
                    key={i}
                    className={`px-3 py-1 rounded-full text-sm ${
                      attendee.is_remote 
                        ? 'bg-blue-500/20 text-blue-300' 
                        : 'bg-green-500/20 text-green-300'
                    }`}
                  >
                    {attendee.name}
                    {attendee.is_remote && ' (Remote)'}
                  </span>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Action Items from this meeting */}
        {selectedMeeting.action_items?.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <ListTodo className="w-5 h-5 mr-2 text-red-400" />
              Action Items ({selectedMeeting.action_items.length})
            </h2>
            
            <div className="space-y-3">
              {selectedMeeting.action_items.map((item, i) => (
                <div 
                  key={i} 
                  className={`p-4 rounded-lg border ${getActionStatusColor(item.status)}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-white font-medium">{item.task}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                        <span className="text-yellow-400">Owner: {item.owner}</span>
                        {item.due_date && (
                          <span className="text-slate-400">Due: {item.due_date}</span>
                        )}
                      </div>
                      {item.notes && (
                        <p className="text-sm text-slate-400 mt-2">{item.notes}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${getActionStatusColor(item.status)}`}>
                      {item.status || 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Transcript */}
        {selectedMeeting.transcript?.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-purple-400" />
              Transcript
            </h2>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {selectedMeeting.transcript.map((segment, i) => (
                <div key={i} className="flex gap-3">
                  <span className="font-semibold text-yellow-400 min-w-[120px]">
                    {segment.speaker_name}:
                  </span>
                  <span className="text-slate-300">{segment.text}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Generated Minutes */}
        {selectedMeeting.status === 'processing' && (
          <Card className="p-6 mb-6 bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border-yellow-500/30">
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500 mr-3" />
              <span className="text-white">Generating meeting minutes...</span>
            </div>
          </Card>
        )}

        {selectedMeeting.minutes && (
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-green-400" />
                {selectedMeeting.status === 'historical' ? 'Extracted Minutes' : 'AI-Generated Minutes'}
              </h2>
              <div className="flex gap-2">
                <Button 
                  onClick={analyzeActionsWithAI}
                  disabled={analyzingActions}
                  variant="outline" 
                  size="sm" 
                  className="border-green-500 text-green-400"
                >
                  {analyzingActions ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      AI Update Actions
                    </>
                  )}
                </Button>
                <Button 
                  onClick={() => setShowEmailDialog(true)} 
                  variant="outline" 
                  size="sm" 
                  className="border-blue-500 text-blue-400"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email PDF
                </Button>
                <Button onClick={regenerateMinutes} variant="ghost" size="sm" className="text-slate-400">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              </div>
            </div>
            
            {/* Summary */}
            {selectedMeeting.minutes.summary && (
              <div className="mb-6">
                <h3 className="font-semibold text-yellow-400 mb-2">Summary</h3>
                <p className="text-slate-300">{selectedMeeting.minutes.summary}</p>
              </div>
            )}
            
            {/* Key Points */}
            {selectedMeeting.minutes.key_points?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-yellow-400 mb-2">Key Discussion Points</h3>
                <ul className="space-y-1">
                  {selectedMeeting.minutes.key_points.map((point, i) => (
                    <li key={i} className="text-slate-300 flex items-start">
                      <span className="text-yellow-500 mr-2">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Decisions */}
            {selectedMeeting.minutes.decisions?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-green-400 mb-2">Decisions Made</h3>
                <ul className="space-y-1">
                  {selectedMeeting.minutes.decisions.map((decision, i) => (
                    <li key={i} className="text-slate-300 flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-400 mr-2 mt-0.5" />
                      {decision}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Next Steps */}
            {selectedMeeting.minutes.next_steps?.length > 0 && (
              <div>
                <h3 className="font-semibold text-blue-400 mb-2">Next Steps</h3>
                <ul className="space-y-1">
                  {selectedMeeting.minutes.next_steps.map((step, i) => (
                    <li key={i} className="text-slate-300 flex items-start">
                      <span className="text-blue-500 mr-2">→</span>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )}

        {/* Minutes Error */}
        {selectedMeeting.minutes_error && (
          <Card className="p-6 mb-6 bg-red-500/10 border-red-500/30">
            <div className="flex items-center text-red-400">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>Error generating minutes: {selectedMeeting.minutes_error}</span>
            </div>
            <Button onClick={regenerateMinutes} className="mt-3 bg-red-500 hover:bg-red-600">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </Card>
        )}

        {/* Email Minutes Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Mail className="w-5 h-5 mr-2 text-blue-400" />
                Email Meeting Minutes
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <p className="text-slate-400 text-sm">
                Send the meeting minutes to attendees or other recipients.
              </p>
              
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">
                  Email Addresses
                </label>
                <Textarea
                  placeholder="Enter email addresses separated by commas&#10;e.g., john@example.com, jane@example.com"
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  className="bg-slate-800 border-slate-700 min-h-[80px]"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leave empty to auto-send to meeting attendees (if they have accounts)
                </p>
              </div>
              
              <Button
                onClick={emailMinutes}
                disabled={sendingEmail}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Minutes
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pre-Meeting Brief Dialog */}
        <Dialog open={showBriefDialog} onOpenChange={setShowBriefDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-purple-400" />
                Pre-Meeting Brief
              </DialogTitle>
            </DialogHeader>
            
            {preMeetingBrief && (
              <div className="space-y-6 mt-4">
                {/* Summary */}
                <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {preMeetingBrief.total_outstanding} Outstanding Actions
                  </h3>
                  <p className="text-slate-400 text-sm">
                    Items requiring follow-up from previous meetings
                  </p>
                </div>
                
                {/* By Owner */}
                {preMeetingBrief.by_owner && Object.keys(preMeetingBrief.by_owner).length > 0 && (
                  <div>
                    <h4 className="font-semibold text-yellow-400 mb-3">By Owner</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(preMeetingBrief.by_owner).map(([owner, count]) => (
                        <span key={owner} className="px-3 py-1 bg-slate-800 rounded-full text-sm">
                          <span className="text-white">{owner}</span>
                          <span className="text-yellow-400 ml-2">{count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Action Items */}
                <div>
                  <h4 className="font-semibold text-red-400 mb-3">Outstanding Items</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {preMeetingBrief.items?.map((item, i) => (
                      <div key={i} className="p-3 bg-slate-800/50 rounded border border-slate-700">
                        <p className="text-white text-sm">{item.task}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span>Owner: <span className="text-yellow-400">{item.owner}</span></span>
                          <span>From: {item.meeting_title?.slice(0, 30)}...</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={() => setShowBriefDialog(false)}
                  className="w-full bg-purple-500 hover:bg-purple-600"
                >
                  Close Brief
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* AI Action Review Dialog */}
        <Dialog open={showActionReviewDialog} onOpenChange={setShowActionReviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center text-xl">
                <Brain className="w-6 h-6 mr-2 text-green-400" />
                AI Action Item Analysis
              </DialogTitle>
              <p className="text-slate-400 text-sm mt-2">
                Review and approve AI-detected action item updates from the meeting transcript
              </p>
            </DialogHeader>
            
            {actionAnalysis && (
              <div className="space-y-6 mt-4">
                
                {/* Action Updates Section */}
                {actionAnalysis.action_updates && actionAnalysis.action_updates.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="font-semibold text-yellow-400 mb-4 flex items-center">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Updates to Existing Items ({actionAnalysis.action_updates.length})
                    </h3>
                    <div className="space-y-3">
                      {actionAnalysis.action_updates.map((update, idx) => (
                        <div 
                          key={idx}
                          onClick={() => toggleUpdateSelection(idx)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedUpdates.includes(idx) 
                              ? 'bg-green-500/10 border-green-500/50' 
                              : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <input 
                                  type="checkbox" 
                                  checked={selectedUpdates.includes(idx)}
                                  onChange={() => toggleUpdateSelection(idx)}
                                  className="rounded"
                                />
                                <span className="font-medium text-white">{update.original_task}</span>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-slate-400 mb-2">
                                <span>Owner: <span className="text-yellow-400">{update.original_owner}</span></span>
                                <span>→</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  update.suggested_status === 'Done' ? 'bg-green-500/20 text-green-400' :
                                  update.suggested_status === 'Rolled Over' ? 'bg-orange-500/20 text-orange-400' :
                                  update.suggested_status === 'Ongoing' ? 'bg-blue-500/20 text-blue-400' :
                                  'bg-slate-500/20 text-slate-400'
                                }`}>
                                  {update.suggested_status}
                                </span>
                                <span className="text-xs text-slate-500">
                                  ({Math.round(update.confidence * 100)}% confidence)
                                </span>
                              </div>
                              <div className="text-sm text-green-300 bg-green-500/10 p-2 rounded">
                                <strong>Update:</strong> {update.suggested_notes}
                              </div>
                              {update.transcript_excerpt && (
                                <div className="text-xs text-slate-500 mt-2 italic">
                                  "{update.transcript_excerpt}"
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* New Action Items Section */}
                {actionAnalysis.new_action_items && actionAnalysis.new_action_items.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="font-semibold text-blue-400 mb-4 flex items-center">
                      <Plus className="w-4 h-4 mr-2" />
                      New Action Items Detected ({actionAnalysis.new_action_items.length})
                    </h3>
                    <div className="space-y-3">
                      {actionAnalysis.new_action_items.map((item, idx) => (
                        <div 
                          key={idx}
                          onClick={() => toggleNewItemSelection(idx)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedNewItems.includes(idx) 
                              ? 'bg-blue-500/10 border-blue-500/50' 
                              : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className="flex items-start">
                            <input 
                              type="checkbox" 
                              checked={selectedNewItems.includes(idx)}
                              onChange={() => toggleNewItemSelection(idx)}
                              className="rounded mr-3 mt-1"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-white">{item.task}</p>
                              <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                                <span>Owner: <span className="text-yellow-400">{item.owner}</span></span>
                                {item.due_date && <span>Due: <span className="text-red-400">{item.due_date}</span></span>}
                              </div>
                              {item.source_excerpt && (
                                <div className="text-xs text-slate-500 mt-2 italic">
                                  "{item.source_excerpt}"
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Rollover Items Section */}
                {actionAnalysis.rollover_items && actionAnalysis.rollover_items.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="font-semibold text-orange-400 mb-4 flex items-center">
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Items to Roll Over ({actionAnalysis.rollover_items.length})
                    </h3>
                    <div className="space-y-3">
                      {actionAnalysis.rollover_items.map((item, idx) => (
                        <div 
                          key={idx}
                          onClick={() => toggleRolloverSelection(item.action_id)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedRollovers.includes(item.action_id) 
                              ? 'bg-orange-500/10 border-orange-500/50' 
                              : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className="flex items-start">
                            <input 
                              type="checkbox" 
                              checked={selectedRollovers.includes(item.action_id)}
                              onChange={() => toggleRolloverSelection(item.action_id)}
                              className="rounded mr-3 mt-1"
                            />
                            <div className="flex-1">
                              <p className="text-sm text-slate-400">Item ID: {item.action_id.slice(0, 8)}...</p>
                              <p className="text-white">{item.reason}</p>
                              {item.new_due_date && (
                                <p className="text-xs text-orange-400 mt-1">New due date: {item.new_due_date}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Items Not Discussed */}
                {actionAnalysis.items_not_discussed && actionAnalysis.items_not_discussed.length > 0 && (
                  <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                    <h4 className="font-medium text-slate-400 mb-2">
                      Items Not Discussed ({actionAnalysis.items_not_discussed.length})
                    </h4>
                    <p className="text-xs text-slate-500">
                      These outstanding items were not mentioned in the meeting transcript
                    </p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-700">
                  <Button
                    onClick={applyActionUpdates}
                    disabled={applyingActions || (selectedUpdates.length === 0 && selectedNewItems.length === 0 && selectedRollovers.length === 0)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    {applyingActions ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Apply Selected ({selectedUpdates.length + selectedNewItems.length + selectedRollovers.length})
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowActionReviewDialog(false)}
                    variant="outline"
                    className="border-slate-600"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
